import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { apiAssetUrl } from '../../config'
import { adminApi } from '../api'
import ImageUpload from '../ImageUpload'
import { IMAGE_SIZE_HINTS } from '../media'
import { Btn, Card, Field, FormSkeleton, areaClass, inputClass } from '../ui'
import type { ClientDetail } from './clientsShared'

type FormState = {
  name: string
  industry: string
  description: string
  website_url: string
  is_active: boolean
  sort_order: string
  logo_path: string | null
  logo_stored_path: string | null
}

const empty = (): FormState => ({
  name: '',
  industry: '',
  description: '',
  website_url: '',
  is_active: true,
  sort_order: '0',
  logo_path: null,
  logo_stored_path: null,
})

export default function ClientFormPage() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(empty())
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (isNew) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const { res, data } = await adminApi<{ client?: ClientDetail }>(`/api/admin/website/clients/${id}`)
      if (cancelled) return
      const c = data.client
      if (!res.ok || !c) {
        setErr('Client not found')
        setLoading(false)
        return
      }
      setForm({
        name: String(c.name || ''),
        industry: String(c.industry || ''),
        description: String(c.description || ''),
        website_url: String(c.website_url || ''),
        is_active: !!c.is_active,
        sort_order: String(c.sort_order ?? 0),
        logo_path: c.logo_path || null,
        logo_stored_path: c.logo_stored_path || null,
      })
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  async function save(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSaving(true)
    const fd = new FormData()
    fd.set('name', form.name)
    fd.set('industry', form.industry)
    fd.set('description', form.description)
    fd.set('website_url', form.website_url)
    fd.set('is_active', form.is_active ? '1' : '0')
    fd.set('sort_order', form.sort_order)
    fd.set('logo_path', form.logo_stored_path || form.logo_path || '')

    const path = isNew ? '/api/admin/website/clients' : `/api/admin/website/clients/${id}`
    const { res, data } = await adminApi<{ message?: string; id?: number }>(path, {
      method: isNew ? 'POST' : 'PUT',
      formData: fd,
    })
    setSaving(false)
    if (!res.ok) {
      setErr(data.message || 'Save failed')
      return
    }
    navigate('/admin/clients', { replace: true })
  }

  if (loading) return <FormSkeleton />

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Btn variant="ghost" onClick={() => navigate('/admin/clients')}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Btn>
      </div>

      <form
        onSubmit={save}
        className="grid xl:grid-cols-[minmax(0,1fr)_300px] gap-3 sm:gap-4 items-start"
      >
        <Card className="!p-4 sm:!p-5 space-y-3 sm:space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name">
              <input
                className={inputClass}
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Client / brand name"
              />
            </Field>
            <Field label="Industry">
              <input
                className={inputClass}
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                placeholder="e.g. Real Estate"
              />
            </Field>
          </div>
          <Field label="Description">
            <textarea
              className={areaClass}
              style={{ minHeight: 100 }}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional short note"
            />
          </Field>
          <Field label="Website URL">
            <div className="relative">
              <ExternalLink className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className={`${inputClass} !pl-9`}
                value={form.website_url}
                onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                placeholder="https://…"
              />
            </div>
          </Field>

          {err && (
            <p className="text-sm text-red-600 rounded-xl bg-red-50 border border-red-100 px-3 py-2">
              {err}
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-primary/10 xl:hidden">
            <Btn type="submit" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Add client' : 'Save changes'}
            </Btn>
            <Btn variant="ghost" onClick={() => navigate('/admin/clients')}>
              Cancel
            </Btn>
          </div>
        </Card>

        <aside className="space-y-3 xl:sticky xl:top-20 order-first xl:order-none">
          <Card className="!p-0 overflow-hidden">
            <div
              className={`px-4 py-3 border-b border-primary/10 ${
                form.is_active
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-500'
                  : 'bg-gradient-to-r from-navy-900 to-navy-800'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white/70">
                    Visibility
                  </p>
                  <p className="text-sm font-bold text-white">
                    {form.is_active ? 'Active on site' : 'Hidden'}
                  </p>
                </div>
                <div className="inline-flex rounded-xl bg-black/20 p-0.5 backdrop-blur">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: false })}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      !form.is_active
                        ? 'bg-white text-navy-900 shadow'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Hidden
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: true })}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      form.is_active
                        ? 'bg-white text-emerald-700 shadow'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Active
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <ImageUpload
                label="Logo"
                purpose="logo"
                previewFit="contain"
                aspectClass="aspect-[2/1]"
                hint={IMAGE_SIZE_HINTS.logo}
                value={form.logo_path}
                onChange={({ storedPath, url }) =>
                  setForm({
                    ...form,
                    logo_path: url || null,
                    logo_stored_path: storedPath,
                  })
                }
              />

              {form.logo_path && (
                <div className="flex items-center gap-3 rounded-2xl border border-primary/10 bg-slate-50/80 p-2.5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white p-1.5 border border-primary/10">
                    <img
                      src={apiAssetUrl(form.logo_path)}
                      alt=""
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy-900 truncate">{form.name || 'Preview'}</p>
                    <p className="text-xs text-slate-500 truncate">{form.industry || 'Industry'}</p>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Sort order
                </span>
                <input
                  className={inputClass}
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  inputMode="numeric"
                />
                <p className="text-[0.7rem] text-slate-400">Or drag on the list page</p>
              </div>

              <div className="[&_button]:w-full">
                <Btn type="submit" disabled={saving}>
                  {saving ? 'Saving…' : isNew ? 'Add client' : 'Save changes'}
                </Btn>
              </div>
            </div>
          </Card>
        </aside>
      </form>
    </div>
  )
}
